import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, ChevronDown } from "lucide-react";
import { api, gbp, formatApiError, openPrivateFile } from "@/lib/api";
import { STATUS_OPTIONS, STATUS_LABELS } from "@/lib/constants";
import { StatusBadge } from "@/components/site/StatusBadge";

const date = (s) => new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const SUMMARY = {
  buyers: (b) => `Budget ${b.budget_min ? `${gbp(b.budget_min)} – ` : "up to "}${gbp(b.budget_max)} · ${b.timeline}`,
  sellers: (s) => `${s.property_address}, ${s.postcode} · ${s.property_type} · ${s.bedrooms} bed`,
  enquiries: (q) => `Re: ${q.property_title}`,
};

const DETAILS = {
  buyers: (b) => [["Chain status", b.chain_status], ["Ownership", b.ownership_status], ["Proof type", b.proof_type],
    ["Areas", b.target_areas?.join(", ")], ["Property types", b.property_types?.join(", ")], ["Min bedrooms", b.bedrooms_min], ["Notes", b.notes]],
  sellers: (s) => [["Area", s.borough], ["Tenure", s.tenure], ["Price expectation", gbp(s.asking_price)], ["Timeline", s.timeline],
    ["Reason", s.reason], ["Vacant", s.is_vacant == null ? "" : s.is_vacant ? "Yes" : "No"], ["Notes", s.notes]],
  enquiries: (q) => [["Message", q.message]],
};

const LeadRow = ({ kind, lead, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(lead.admin_notes || "");

  const update = async (status, admin_notes) => {
    try {
      const { data } = await api.patch(`/admin/leads/${kind}/${lead.id}`, { status, admin_notes });
      onUpdated(data);
      toast.success(`Updated: ${STATUS_LABELS[status]}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`admin-lead-${lead.id}`} className="bg-white border border-[#E4E1D9]">
      <div className="p-5 flex flex-col md:flex-row md:items-center gap-4">
        <button onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left" data-testid={`admin-lead-toggle-${lead.id}`}>
          <p className="font-medium flex items-center gap-2">{lead.full_name}
            <ChevronDown className={`h-4 w-4 text-[#847F77] transition-transform ${open ? "rotate-180" : ""}`} /></p>
          <p className="text-sm text-[#57534E] truncate">{SUMMARY[kind](lead)}</p>
          <p className="font-plex text-xs text-[#847F77] mt-1">{lead.email}{lead.phone ? ` · ${lead.phone}` : ""} · {date(lead.created_at)}</p>
        </button>
        <div className="flex items-center gap-3">
          {kind === "buyers" && (
            <>
              <button onClick={() => openPrivateFile(lead.proof_file_id).catch((e) => toast.error(formatApiError(e)))}
                data-testid={`admin-view-proof-${lead.id}`} className="h-9 px-3 inline-flex items-center gap-1.5 border border-[#E4E1D9] text-xs hover:border-[#9F7E44]">
                <FileText className="h-4 w-4" /> View proof
              </button>
              {lead.status === "pending_review" && (
                <button onClick={() => update("verified")} data-testid={`admin-btn-verify-aip-${lead.id}`}
                  className="h-9 px-3 bg-[#065F46] text-white text-xs hover:bg-[#047857]">Verify funds</button>
              )}
            </>
          )}
          <select data-testid={`admin-status-dropdown-${lead.id}`} value={lead.status} onChange={(e) => update(e.target.value)}
            className="field-input field-select !h-9 !w-40 text-xs">
            {STATUS_OPTIONS[kind].map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
          </select>
        </div>
      </div>
      {open && (
        <div className="border-t border-[#E4E1D9] p-5 bg-[#F7F6F2] grid md:grid-cols-2 gap-6">
          <dl className="space-y-2 text-sm">
            {DETAILS[kind](lead).filter(([, v]) => v).map(([k, v]) => (
              <div key={k} className="grid grid-cols-3 gap-3"><dt className="text-[#847F77]">{k}</dt><dd className="col-span-2">{v}</dd></div>
            ))}
            <div className="pt-2"><StatusBadge status={lead.status} /></div>
          </dl>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#57534E] mb-2">Private notes</p>
            <textarea data-testid={`admin-notes-${lead.id}`} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="field-input h-auto py-3" />
            <button onClick={() => update(lead.status, notes)} data-testid={`admin-save-notes-${lead.id}`}
              className="mt-2 h-9 px-4 bg-[#121417] text-white text-xs hover:bg-[#9F7E44]">Save notes</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const LeadsList = ({ kind, onChange }) => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    api.get(`/admin/leads/${kind}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [kind]);
  useEffect(load, [load]);

  const onUpdated = (row) => { setItems((xs) => xs.map((x) => (x.id === row.id ? row : x))); onChange?.(); };
  const list = (items || []).filter((x) => !filter || x.status === filter);

  return (
    <div>
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-[#57534E]">{list.length} shown</p>
        <select data-testid={`admin-filter-${kind}`} value={filter} onChange={(e) => setFilter(e.target.value)} className="field-input field-select !w-48 !h-10">
          <option value="">All statuses</option>
          {STATUS_OPTIONS[kind].map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
      </div>
      <div className="mt-5 space-y-3">
        {items === null ? <p className="eyebrow">Loading…</p> : list.length === 0
          ? <p className="text-sm text-[#847F77] border border-dashed border-[#C5BCAB] p-6" data-testid={`admin-empty-${kind}`}>Nothing here yet.</p>
          : list.map((l) => <LeadRow key={l.id} kind={kind} lead={l} onUpdated={onUpdated} />)}
      </div>
    </div>
  );
};
